import { normalizeClinicalFlag, normalizeClinicalFlags } from './clinicalFlags';
import { normalizeHandoverNote, normalizeHandoverPatientEntry } from './handoverUtils';
import { getDischargeNoteBody } from './dischargeNoteBody';
function ensureArray(value) {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
}

function normalizePatient(note) {
  if (!note.patient) {
    note.patient = {
      name: null,
      room: null,
      age: null,
      sex: null,
      details_source: 'transcript',
    };
  } else {
    note.patient.details_source = note.patient.details_source || 'transcript';
  }
  return note;
}

function normalizeFlags(note) {
  note.flags = normalizeClinicalFlags(note.flags);
  return note;
}

function toNumberOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isNaN(num) ? value : num;
}

function normalizeVitalsObject(vitals = {}) {
  return {
    bp_systolic: toNumberOrNull(vitals.bp_systolic),
    bp_diastolic: toNumberOrNull(vitals.bp_diastolic),
    pulse: toNumberOrNull(vitals.pulse),
    temp_celsius: toNumberOrNull(vitals.temp_celsius),
    o2_sat: toNumberOrNull(vitals.o2_sat),
    rr: toNumberOrNull(vitals.rr),
    pain: toNumberOrNull(vitals.pain),
    weight_kg: toNumberOrNull(vitals.weight_kg),
    blood_sugar: toNumberOrNull(vitals.blood_sugar),
  };
}

export function validateProgressNote(json) {
  const note = normalizeFlags({ ...json });
  note.negations_detected = ensureArray(note.negations_detected);

  if (note.clinical_content === false) {
    return normalizePatient(note);
  }

  if (!note.soap) note.soap = {};

  const sections = ['subjective', 'objective', 'assessment', 'plan'];
  sections.forEach((section) => {
    if (!note.soap[section] || String(note.soap[section]).trim() === '') {
      note.soap[section] = note.soap[section] || '';
    }
  });

  if (!note.vitals_extracted) note.vitals_extracted = {};
  note.outstanding_tasks = ensureArray(note.outstanding_tasks);

  return normalizePatient(note);
}

export function validateVitals(json) {
  const note = normalizeFlags({ ...json });
  note.vitals = normalizeVitalsObject(note.vitals || {});
  note.outstanding_tasks = ensureArray(note.outstanding_tasks);
  return normalizePatient(note);
}

function validateHandoverPatientEntry(entry) {
  const patientEntry = normalizeHandoverPatientEntry(entry);
  patientEntry.flags = normalizeClinicalFlags(patientEntry.flags);
  if (!patientEntry.sbar) {
    patientEntry.sbar = {
      situation: '',
      background: '',
      assessment: '',
      recommendation: '',
    };
  }
  return patientEntry;
}

export function validateHandover(json) {
  const note = normalizeHandoverNote({ ...json });
  note.patients = note.patients.map(validateHandoverPatientEntry);
  note.flags = note.patients.flatMap((entry) => entry.flags || []);
  return normalizeFlags(note);
}

function normalizeMarMed(med) {
  const confidence = ['high', 'medium', 'low'].includes(med?.confidence)
    ? med.confidence
    : 'medium';

  return {
    drug_name: med?.drug_name || med?.name || '',
    name: med?.drug_name || med?.name || '',
    dose: med?.dose || null,
    dose_amount: med?.dose_amount ?? null,
    dose_unit: med?.dose_unit ?? null,
    route: med?.route || '',
    time_given: med?.time_given ?? null,
    indication: med?.indication ?? null,
    patient_response: med?.patient_response || med?.response || null,
    response: med?.patient_response || med?.response || null,
    adverse_reaction: Boolean(med?.adverse_reaction),
    adverse_reaction_details: med?.adverse_reaction_details || null,
    pain_before: med?.pain_before ?? null,
    pain_after: med?.pain_after ?? null,
    bp_before: med?.bp_before ?? null,
    bp_systolic_before: med?.bp_systolic_before ?? null,
    bp_diastolic_before: med?.bp_diastolic_before ?? null,
    pulse_before: med?.pulse_before ?? null,
    blood_sugar_before: med?.blood_sugar_before ?? null,
    blood_sugar_after: med?.blood_sugar_after ?? null,
    inr: med?.inr ?? null,
    injection_site: med?.injection_site ?? null,
    infusion_rate: med?.infusion_rate ?? null,
    volume: med?.volume ?? null,
    not_given: Boolean(med?.not_given),
    scheduled_time: med?.scheduled_time ?? null,
    deviation_reason_id: med?.deviation_reason_id ?? null,
    deviation_reason: med?.deviation_reason ?? null,
    nurse_id: med?.nurse_id ?? null,
    documented_at: med?.documented_at ?? null,
    confidence,
  };
}

export function validateMedicationAdmin(json) {
  const note = normalizeFlags({ ...json });
  note.negations_detected = ensureArray(note.negations_detected);

  const sourceMeds = note.medications_administered?.length
    ? note.medications_administered
    : note.medications || [];

  const medications = sourceMeds.map(normalizeMarMed).filter((med) => med.drug_name);

  note.medications_administered = medications;
  note.medications = medications;
  note.unverified_medications = medications.filter((m) => m.confidence === 'low');
  note.outstanding_tasks = ensureArray(note.outstanding_tasks);

  return normalizePatient(note);
}

function normalizeFluidInput(entry) {
  return {
    time_given: entry?.time_given ?? null,
    type: entry?.type || 'Other',
    type_other: entry?.type_other ?? null,
    volume_ordered_ml: entry?.volume_ordered_ml ?? entry?.volume_ordered ?? null,
    volume_infused_ml: entry?.volume_infused_ml ?? entry?.volume_infused ?? null,
    rate_ml_hr: entry?.rate_ml_hr ?? entry?.rate ?? null,
    running: entry?.running !== false,
  };
}

function normalizeFluidOutput(entry) {
  return {
    time_given: entry?.time_given ?? null,
    type: entry?.type || 'Other',
    type_other: entry?.type_other ?? null,
    volume_ml: entry?.volume_ml ?? entry?.volume ?? null,
  };
}

export function hasFluidEntries(note) {
  const inputs = note?.fluid_inputs || [];
  const outputs = note?.fluid_outputs || [];
  const hasInput = inputs.some(
    (e) => e.volume_infused_ml != null || e.volume_infused != null
  );
  const hasOutput = outputs.some((e) => e.volume_ml != null || e.volume != null);
  return hasInput || hasOutput;
}

export function validateFluidBalance(json) {
  const note = normalizeFlags({ ...json });
  note.fluid_inputs = ensureArray(note.fluid_inputs).map(normalizeFluidInput);
  note.fluid_outputs = ensureArray(note.fluid_outputs).map(normalizeFluidOutput);
  return normalizePatient(note);
}

export function validateCarePlan(json) {
  const note = normalizeFlags({ ...json });
  note.negations_detected = ensureArray(note.negations_detected);
  note.outstanding_tasks = ensureArray(note.outstanding_tasks);
  const plan = note.care_plan || {};
  note.care_plan = {
    nursing_diagnosis: String(plan.nursing_diagnosis || '').trim() || 'Not documented.',
    goals: String(plan.goals || '').trim() || 'Not documented.',
    interventions: ensureArray(plan.interventions)
      .map((item) => String(item || '').trim())
      .filter(Boolean),
    evaluation:
      String(plan.evaluation || '').trim() || 'To be evaluated at next assessment.',
  };
  note.status = note.status === 'resolved' ? 'resolved' : 'active';
  return normalizePatient(note);
}

export function validateDischarge(json) {
  const note = normalizeFlags({ ...json });
  note.education = ensureArray(note.education);
  note.discharge_medications = ensureArray(note.discharge_medications);
  note.follow_up = note.follow_up || { physician: '', timeframe: '', location: '' };
  note.understanding_patient = Boolean(note.understanding_patient);
  note.understanding_family = Boolean(note.understanding_family);

  const narrative = typeof note.narrative === 'string' ? note.narrative.trim() : '';
  if (!narrative) {
    const flattened = getDischargeNoteBody({ ...note, narrative: '' });
    if (flattened) note.narrative = flattened;
  } else {
    note.narrative = narrative;
  }

  return normalizePatient(note);
}

export function countPopulatedVitals(vitalsNote) {
  if (!vitalsNote?.vitals) return 0;
  const v = vitalsNote.vitals;
  const keys = [
    'bp_systolic',
    'bp_diastolic',
    'pulse',
    'temp_celsius',
    'o2_sat',
    'rr',
    'pain',
    'weight_kg',
    'blood_sugar',
  ];
  return keys.filter((k) => v[k] != null && v[k] !== '').length;
}

export function validateNote(json, noteType) {
  if (!json) return null;

  let validated;
  switch (noteType) {
    case 'progress_note':
      validated = validateProgressNote(json);
      break;
    case 'vitals':
      validated = validateVitals(json);
      break;
    case 'handover':
      validated = validateHandover(json);
      break;
    case 'medication_admin':
      validated = validateMedicationAdmin(json);
      break;
    case 'fluid_balance':
      validated = validateFluidBalance(json);
      break;
    case 'care_plan':
      validated = validateCarePlan(json);
      break;
    case 'discharge':
      validated = validateDischarge(json);
      break;
    default:
      validated = normalizePatient(normalizeFlags({ ...json }));
  }

  // Late-entry / care_time defaults are applied later with the client encounter clock.
  // Do not use model datetime here — it is usually UTC and produces the wrong local time.
  return validated;
}

export function parseJsonFromResponse(text) {
  if (!text) throw new Error('Empty response from API');

  let cleaned = text.trim();
  if (cleaned.startsWith('```')) {
    cleaned = cleaned.replace(/^```(?:json)?\n?/, '').replace(/\n?```$/, '');
  }

  const start = cleaned.indexOf('{');
  const end = cleaned.lastIndexOf('}');
  if (start === -1 || end === -1) {
    throw new Error('No JSON object found in response');
  }

  return JSON.parse(cleaned.slice(start, end + 1));
}

export { normalizeClinicalFlag, normalizeClinicalFlags };
